// src/pages/Admin/Updates/UpdatesPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import { format } from "date-fns";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

const tagBadge = (tag) => {
  const t = (tag || "").toLowerCase();
  if (t === "bug") return { className: "bg-amber-500", label: "Bug" };
  if (t === "new") return { className: "bg-blue-600", label: "Nouveau" };
  if (t === "update") return { className: "bg-emerald-600", label: "Mise à jour" };
  return { className: "bg-slate-500", label: tag || "—" };
};

export default function RequestsAdmin() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [updatesData, setUpdatesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTag, setFilterTag] = useState(""); // "", "New", "Update", "Bug"
  const [q, setQ] = useState(""); // recherche simple sur titre + contenu

  const fetchUpdate = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://api.monappliclub.com/api/updates/AllUpdates");
      setUpdatesData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des mises à jour :", error);
      toast.error("Erreur lors de la récupération des mises à jour");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpdate();
  }, [fetchUpdate]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return updatesData.filter((u) => {
      const matchTag = !filterTag || (u.tag_update || "").toLowerCase() === filterTag.toLowerCase();
      const matchQ =
        !query ||
        `${u.title_update || ""} ${u.content_update || ""}`.toLowerCase().includes(query);
      return matchTag && matchQ;
    });
  }, [updatesData, filterTag, q]);

  return (
    <section className="w-full">
      {/* Offset fiable pour la navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header */}
        <HeaderTitle
          role="Administrateur"
          title="Mise à jour de l'application"
          align="center"
          className="mb-1"
        />  

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Bandeau dégradé + infos */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h3 className="text-lg sm:text-xl font-semibold text-center">
                Suivez l’évolution de l’application
              </h3>
              <p className="mt-2 text-sm/6 max-w-3xl mx-auto text-center">
                Corrections de <span className="font-semibold">bugs</span>,{" "}
                <span className="font-semibold">mises à jour</span> et{" "}
                <span className="font-semibold">nouvelles fonctionnalités</span> :
                tout est réuni ici pour vous tenir informé.
              </p>

              {/* Barre d’outils : filtre + recherche */}
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
                <select
                  value={filterTag}
                  onChange={(e) => setFilterTag(e.target.value)}
                  className="w-full rounded-xl border border-white/30 bg-white/90 text-slate-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white"
                >
                  <option value="">Tous les tags</option>
                  <option value="New">Nouveau</option>
                  <option value="Update">Mise à jour</option>
                  <option value="Bug">Bug</option>
                </select>
                <div className="sm:col-span-2">
                  <input
                    type="text"
                    placeholder="Rechercher par titre ou contenu…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="w-full rounded-xl border border-white/30 bg-white/90 text-slate-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="px-5 pb-6 sm:px-8">
            {/* Loading skeleton */}
            {loading && (
              <div className="space-y-3 mt-5">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucune mise à jour trouvée
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Modifiez vos filtres ou réessayez plus tard.
                </p>
              </div>
            )}

            {/* TABLE DESKTOP */}
            {!loading && filtered.length > 0 && (
              <div className="hidden md:block mt-5">
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                        {["Date", "Titre", "Contenu", "Tag"].map((h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                      {filtered.map((u) => {
                        const badge = tagBadge(u.tag_update);
                        return (
                          <tr
                            key={u.id}
                            className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-slate-800 dark:text-slate-200">
                              {u.date_update
                                ? format(new Date(u.date_update), "dd/MM/yy")
                                : "—"}
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                              {u.title_update || "—"}
                            </td>
                            <td className="px-4 py-3">
                              <div className="max-h-40 overflow-auto pr-1 text-slate-700 dark:text-slate-300 leading-snug">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: u.content_update || "",
                                  }}
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${badge.className}`}
                              >
                                {badge.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* CARTES MOBILE */}
            {!loading && filtered.length > 0 && (
              <div className="md:hidden mt-5 space-y-3">
                {filtered.map((u) => {
                  const badge = tagBadge(u.tag_update);
                  return (
                    <div
                      key={`card-${u.id}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {u.date_update
                              ? format(new Date(u.date_update), "dd/MM/yy")
                              : "—"}
                          </p>
                          <h3 className="mt-0.5 text-base font-semibold text-slate-900 dark:text-slate-100">
                            {u.title_update || "—"}
                          </h3>
                        </div>
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </div>

                      <div className="mt-2 max-h-60 overflow-auto pr-1 text-sm text-slate-700 dark:text-slate-300">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: u.content_update || "",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>
    </section>
  );
}
